export const PRIVACY_PREFERENCES_KEY = 'x3algo_privacy_preferences'
export const PRIVACY_PREFERENCES_VERSION = 1
export const PRIVACY_NOTICE_VERSION = '2026-07-22'

const PREFERENCE_LIFETIME_MS = 180 * 24 * 60 * 60 * 1000
const OPTIONAL_CATEGORIES = ['experience', 'analytics']

const DEFAULT_CATEGORIES = Object.freeze({
  experience: false,
  analytics: false
})

let runtimeCategories = { ...DEFAULT_CATEGORIES }

function hasGlobalPrivacyControl() {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true
}

function normalizeCategories(categories = {}) {
  const normalized = OPTIONAL_CATEGORIES.reduce(
    (result, category) => ({
      ...result,
      [category]: categories[category] === true
    }),
    {}
  )

  if (hasGlobalPrivacyControl()) {
    normalized.analytics = false
  }

  return normalized
}

function isValidRecord(record) {
  if (!record || typeof record !== 'object') return false
  if (record.version !== PRIVACY_PREFERENCES_VERSION) return false
  if (record.noticeVersion !== PRIVACY_NOTICE_VERSION) return false
  if (!record.categories || typeof record.categories !== 'object') return false

  const expiresAt = Date.parse(record.expiresAt)
  return Number.isFinite(expiresAt) && expiresAt > Date.now()
}

function setRuntimeCategories(categories) {
  runtimeCategories = normalizeCategories(categories)
  window.dispatchEvent(
    new CustomEvent('privacy:preferences-changed', {
      detail: { categories: { ...runtimeCategories } }
    })
  )
}

export function getDefaultPrivacyCategories() {
  return { ...DEFAULT_CATEGORIES }
}

export function loadPrivacyPreferences() {
  if (typeof window === 'undefined') return null

  try {
    const stored = window.localStorage.getItem(PRIVACY_PREFERENCES_KEY)
    if (!stored) {
      runtimeCategories = { ...DEFAULT_CATEGORIES }
      return null
    }

    const record = JSON.parse(stored)
    if (!isValidRecord(record)) {
      window.localStorage.removeItem(PRIVACY_PREFERENCES_KEY)
      runtimeCategories = { ...DEFAULT_CATEGORIES }
      return null
    }

    const categories = normalizeCategories(record.categories)
    const normalizedRecord = { ...record, categories }

    if (record.categories.analytics === true && categories.analytics === false) {
      normalizedRecord.source = 'global-privacy-control'
      normalizedRecord.decision = categories.experience ? 'custom' : 'rejected'
      window.localStorage.setItem(
        PRIVACY_PREFERENCES_KEY,
        JSON.stringify(normalizedRecord)
      )
    }

    runtimeCategories = categories
    return normalizedRecord
  } catch (_error) {
    runtimeCategories = { ...DEFAULT_CATEGORIES }
    return null
  }
}

export function savePrivacyPreferences(categories, source = 'preferences') {
  const decidedAt = new Date()
  const normalizedCategories = normalizeCategories(categories)
  const enabledCount = OPTIONAL_CATEGORIES.filter(
    category => normalizedCategories[category]
  ).length
  const record = {
    version: PRIVACY_PREFERENCES_VERSION,
    noticeVersion: PRIVACY_NOTICE_VERSION,
    decision:
      enabledCount === 0
        ? 'rejected'
        : enabledCount === OPTIONAL_CATEGORIES.length
          ? 'accepted'
          : 'custom',
    source,
    categories: normalizedCategories,
    decidedAt: decidedAt.toISOString(),
    expiresAt: new Date(decidedAt.getTime() + PREFERENCE_LIFETIME_MS).toISOString()
  }

  try {
    window.localStorage.setItem(PRIVACY_PREFERENCES_KEY, JSON.stringify(record))
  } catch (_error) {
    // The in-memory decision still applies for this page when storage is unavailable.
  }

  setRuntimeCategories(normalizedCategories)
  return record
}

export function isPrivacyCategoryAllowed(category) {
  return runtimeCategories[category] === true
}

export function isGlobalPrivacyControlEnabled() {
  return hasGlobalPrivacyControl()
}

export function syncRuntimePrivacyPreferences(record) {
  setRuntimeCategories(record?.categories || DEFAULT_CATEGORIES)
}
